/* eslint-disable max-len */
/* eslint-disable max-lines */
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { IReservationV2 } from "../../interfaces/reservations/Reservations.types";
import { resendJourneyReservationEmail } from "../../services/Reservations.service";
import { cx } from "../../utils/classnames";
import { isTicketCanceled, TicketStatusEnum } from "../reservations/res.enum";
import Radio from "../shared/svgs/Radio";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import useCan from "../users/permissions/useCan";
import PaymentInfo from "./PaymentInfo";
import UsersInfo from "./UsersInfo";

const Ticket: FC<
  IReservationV2 & {
    onLuggageChange?: () => void;
    onPassengersChange?: () => void;
  }
> = ({
  insertDate,
  journeyGuid,
  oneWayTicketDetails,
  paymentMethodId,
  paymentStatus,
  price,
  roundWayTicketDetails,
  uniqueJourneyId,
  conciergeName,
  flexType,
  hasTextMeMyTrip,
  clientNames,
  hasOpenReturn,
  hasLuggage,
  onLuggageChange,
  onPassengersChange
}) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const can = useCan("reservation.old.ticket.update");

  // An active booking that lost one of its tickets reads as confirmed in the list, which is only
  // half the story. The ticket rows are already in this response, so no extra request is needed.
  const partiallyCanceled =
    paymentStatus === TicketStatusEnum.Confirmed &&
    [...(oneWayTicketDetails || []), ...(roundWayTicketDetails || [])].some((t) =>
      isTicketCanceled(t.statusId)
    );

  const handleCardClick = () => {
    if (can) {
      navigate(`edit/${uniqueJourneyId}`, { state });
    }
  };

  return (
    <div
      className={cx(["ticket", can ? "cursor-pointer" : "cursor-not-allowed"])}
      onClick={handleCardClick}
    >
      <div className="ticket">
        <div className="p-3 md:p-4">
          <div className="h-full w-full flex flex-col gap-3">
            {/* User Details and Payment Info Row */}
            <div className="grid grid-cols-2 md:grid-cols-10 gap-2">
              <div className="col-span-1 md:col-start-1 md:col-span-4">
                <UsersInfo
                  clientNames={clientNames}
                  journeyGuid={journeyGuid}
                  paymentStatus={paymentStatus}
                  uniqueJourneyId={uniqueJourneyId}
                  canUpdate={can}
                />
              </div>
              <div className="col-span-1 md:col-start-7 md:col-span-4">
                <div className="flex items-start gap-2 justify-end">
                  {/* Email Resend Button - Left of payment info */}
                  {can && paymentStatus === 1 && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <DeleteModal
                        params={uniqueJourneyId}
                        title="Konfirmo ri-dërgimin e email-it"
                        paragraph="Ky veprim do të dërgojë përsëri email-in e konfirmimit të biletës."
                        handleDelete={resendJourneyReservationEmail}
                        successMessage="Email-i u dërgua përsëri me sukses"
                        errorMessage="Ndodhi një gabim gjatë dërgimit të email-it"
                        loadingMessage="Duke dërguar email-in..."
                        indicator={
                          <IconBtn className="w-8 h-8 bg-gray-100 hover:bg-gray-200">
                            <div
                              data-tooltip-content="Ridërgo email-in e konfirmimit"
                              data-tooltip-id={`resendEmail-${uniqueJourneyId}`}
                            >
                              <EnvelopeIcon className="w-4 h-4 text-blue-500 hover:text-blue-600" />
                            </div>
                            <Tooltip id={`resendEmail-${uniqueJourneyId}`} className="z-50" />
                          </IconBtn>
                        }
                      />
                    </span>
                  )}
                  <PaymentInfo
                    paymentMethodId={paymentMethodId}
                    paymentStatus={paymentStatus}
                    price={price}
                    clientNames={clientNames}
                    insertDate={insertDate}
                    conciergeName={conciergeName}
                    flexType={flexType}
                    hasTextMeMyTrip={hasTextMeMyTrip}
                    hasOpenReturn={hasOpenReturn}
                    hasLuggage={hasLuggage}
                    uniqueJourneyId={uniqueJourneyId}
                    onLuggageChange={onLuggageChange}
                    onPassengersChange={onPassengersChange}
                    partiallyCanceled={partiallyCanceled}
                  />
                </div>
              </div>
            </div>

            {/* Journey Details */}
            <div className="w-full">
              <div className="bg-white px-3 md:px-4 py-3 flex flex-col md:flex-row rounded-lg gap-4 md:gap-6">
                <div className="text-sm flex items-center justify-center md:justify-start flex-none">
                  <div className="flex flex-col text-center md:text-left">
                    {oneWayTicketDetails[0].operatorName}
                    <div className="text-sm items-center font-light">
                      {dayjs(oneWayTicketDetails[0].journeyDate).format(
                        "DD, MMMM YYYY"
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm w-full">
                  <span className="flex items-center mr-2 md:mr-4 text-xs md:text-sm">
                    {oneWayTicketDetails[0].departureStationName.replace(
                      "Stacioni",
                      ""
                    )}
                  </span>
                  <Radio className="w-3 h-3 text-primary" />
                  <span className="h-[1px] bg-[#eee] flex-1 relative flex justify-center">
                    <div className="flex justify-center absolute -top-3">
                      <span
                        className={`
                      rounded-md  border border-gray-200 bg-white text-xs flex
                      ${
                        oneWayTicketDetails.length > 1 ||
                        roundWayTicketDetails.length > 1
                          ? "py-0.5 px-1"
                          : ""
                      }`}
                      >
                        {oneWayTicketDetails.length > 1 ? (
                          <span>{oneWayTicketDetails.length - 1} Transfer</span>
                        ) : null}
                        {roundWayTicketDetails.length ? (
                          <div className="mx-2">
                            {oneWayTicketDetails.length > 1 && (
                              <span className="mr-2 bg-gray p-[0.5px]" />
                            )}
                            Vajtje - Ardhje
                          </div>
                        ) : null}
                      </span>
                    </div>
                  </span>
                  <Radio className="w-3 h-3 text-gray" />
                  <span className="flex items-center ml-2 md:ml-4 text-xs md:text-sm">
                    {oneWayTicketDetails[
                      oneWayTicketDetails.length - 1
                    ].arrivalStationName.replace("Stacioni", "")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
